import {
  Document, Packer, Paragraph, TextRun, AlignmentType,
  PageBreak, PageOrientation, convertInchesToTwip,
} from "docx";

/**
 * Build a Word document for the front matter. Supports two styles:
 *
 *   "rich"   — mirrors the PDF: centred uppercase half-title, italic
 *              subtitle on title page, bottom-anchored copyright block.
 *              Use this if the author wants the docx to look like a
 *              finished book.
 *
 *   "simple" — left-aligned plain text with paragraph breaks. Easy to
 *              restyle in Word; ideal as a starting point that the
 *              author will tweak.
 *
 * Both variants emit one Word section per logical "page" (page break
 * between each) sized at the chosen trim. Word page-size + margins are
 * specified in twips (1 inch = 1440 twips).
 *
 * Returns a Promise resolving to a Blob suitable for saveAs.
 */

const inToTwip = convertInchesToTwip;

function gutterTwip(targetPages) {
  // KDP gutter recommendation lookup, identical to the PDF generator.
  if (targetPages <= 150) return inToTwip(0.375);
  if (targetPages <= 300) return inToTwip(0.5);
  if (targetPages <= 500) return inToTwip(0.625);
  if (targetPages <= 700) return inToTwip(0.75);
  return inToTwip(0.875);
}

function paragraphsForPage(type, form, style) {
  const RICH = style === "rich";
  const center = AlignmentType.CENTER;
  const left = AlignmentType.LEFT;

  if (type === "blank" || type === "padding") {
    return [new Paragraph({ children: [new TextRun(" ")] })];
  }

  if (type === "half_title") {
    const title = (form.book_title || "Untitled");
    return [
      ...spacerLines(RICH ? 10 : 3),
      new Paragraph({
        alignment: RICH ? center : left,
        children: [new TextRun({
          text: RICH ? title.toUpperCase() : title,
          bold: !RICH,
          font: "Times New Roman",
          size: RICH ? 56 : 32, // half-points; 56 = 28pt
        })],
      }),
    ];
  }

  if (type === "title") {
    const out = [...spacerLines(RICH ? 6 : 2)];
    const titleSize = RICH ? 72 : 36; // 36pt
    out.push(new Paragraph({
      alignment: RICH ? center : left,
      children: [new TextRun({
        text: form.book_title || "Untitled",
        font: "Times New Roman",
        size: titleSize,
      })],
    }));
    if (form.subtitle) {
      out.push(...spacerLines(1));
      out.push(new Paragraph({
        alignment: RICH ? center : left,
        children: [new TextRun({
          text: form.subtitle,
          font: "Times New Roman",
          size: 32,
          italics: RICH,
        })],
      }));
    }
    out.push(...spacerLines(RICH ? 12 : 3));
    if (form.author) {
      out.push(new Paragraph({
        alignment: RICH ? center : left,
        children: [new TextRun({
          text: form.author,
          font: "Times New Roman",
          size: 28,
        })],
      }));
    }
    if (form.publisher) {
      out.push(new Paragraph({
        alignment: RICH ? center : left,
        children: [new TextRun({
          text: form.publisher,
          font: "Times New Roman",
          size: 22,
        })],
      }));
    }
    return out;
  }

  if (type === "copyright") {
    const lines = [];
    const title = form.book_title || "Untitled";
    lines.push(`${title}${form.subtitle ? `: ${form.subtitle}` : ""}`);
    lines.push("");
    const author = form.author || "the author";
    lines.push(`Copyright \u00a9 ${form.copyright_year} by ${author}.`);
    lines.push("");
    if (form.rights_notice) lines.push(form.rights_notice);
    if (form.isbn) {
      lines.push("");
      lines.push(`ISBN: ${form.isbn}`);
    }
    if (form.edition) lines.push(form.edition);
    if (form.publisher) {
      lines.push("");
      lines.push(`Published by ${form.publisher}`);
    }
    if (form.disclaimer) {
      lines.push("");
      lines.push(form.disclaimer);
    }
    const out = [];
    // Push the block toward the bottom in rich mode.
    if (RICH) out.push(...spacerLines(20));
    lines.forEach((line) => {
      out.push(new Paragraph({
        alignment: left,
        children: [new TextRun({
          text: line || " ",
          font: "Times New Roman",
          size: 20, // 10pt
        })],
      }));
    });
    return out;
  }

  if (type === "dedication") {
    const lines = (form.dedication || "").split("\n");
    return [
      ...spacerLines(RICH ? 10 : 3),
      ...lines.map((line) => new Paragraph({
        alignment: RICH ? center : left,
        children: [new TextRun({
          text: line || " ",
          font: "Times New Roman",
          size: 28,
          italics: RICH,
        })],
      })),
    ];
  }

  if (type === "acknowledgments") {
    const out = [
      new Paragraph({
        alignment: RICH ? center : left,
        children: [new TextRun({
          text: "Acknowledgments",
          bold: true,
          font: "Times New Roman",
          size: 40, // 20pt
        })],
      }),
      ...spacerLines(2),
    ];
    (form.acknowledgments || "").split("\n").forEach((line) => {
      out.push(new Paragraph({
        alignment: left,
        children: [new TextRun({
          text: line || " ",
          font: "Times New Roman",
          size: 22,
        })],
      }));
    });
    return out;
  }

  return [new Paragraph({ children: [new TextRun(" ")] })];
}

function spacerLines(count) {
  return Array.from({ length: count }, () =>
    new Paragraph({ children: [new TextRun("")] })
  );
}

export async function buildDocxBlob({ form, pages, padding, trim, style = "rich" }) {
  const allPages = [...pages, ...Array(padding).fill({ type: "padding" })];
  const totalPages = allPages.length;
  const gutter = gutterTwip(totalPages);
  const margin = inToTwip(0.75);

  // Each logical front-matter page becomes its own "section" with a forced
  // page break, then alternating gutter/outer margins for recto/verso.
  const sections = allPages.map((p, idx) => {
    const isRecto = idx % 2 === 0;
    const children = paragraphsForPage(p.type === "copyright_or_blank" ? "copyright" : p.type, form, style);
    // Append a page break paragraph at the end of every non-final section
    // (Word naturally adds one between sections, but we want unambiguous
    // results across viewers).
    if (idx < allPages.length - 1) {
      children.push(new Paragraph({ children: [new PageBreak()] }));
    }
    return {
      properties: {
        page: {
          size: {
            width: inToTwip(trim.w),
            height: inToTwip(trim.h),
            orientation: trim.h >= trim.w ? PageOrientation.PORTRAIT : PageOrientation.LANDSCAPE,
          },
          margin: {
            top: margin,
            bottom: margin,
            left: isRecto ? gutter : margin,
            right: isRecto ? margin : gutter,
          },
        },
      },
      children,
    };
  });

  const doc = new Document({
    creator: "PublisherKit",
    title: form.book_title || "Front matter",
    description: "Front matter generated by PublisherKit",
    sections,
  });

  return await Packer.toBlob(doc);
}
